/**
 * Deal Calculations — pure functions, no React dependency.
 * All percentage inputs are DECIMALS (0.02 = 2%, 0.035 = 3.5%).
 */
import { getLandTransferTax } from '../../../utils/marketDataCalculations';

/** Normalize a percentage value: if > 1, treat as display % and divide by 100 */
function norm(val) {
  if (val == null) return null;
  const n = parseFloat(val);
  return n > 1 ? n / 100 : n;
}

/* ── Kaufnebenkosten ── */
// grunderwerbSteuerOverride: wenn gesetzt (in %, z.B. 3.5 oder 6.5) → Override statt Bundesland-Wert
// null = automatisch aus Bundesland
export function calculateKaufnebenkosten(kaufpreis, bundesland, maklerAnteil = 0.0357, notarAnteil = 0.02, grunderwerbSteuerOverride = null) {
  let gpiEstSatz;
  if (grunderwerbSteuerOverride != null && grunderwerbSteuerOverride > 0) {
    // Override: Anzeige-% (z.B. 3.5 oder 0.035) → normalisieren
    gpiEstSatz = grunderwerbSteuerOverride > 1 ? grunderwerbSteuerOverride / 100 : grunderwerbSteuerOverride;
  } else {
    gpiEstSatz = getLandTransferTax(bundesland);
  }
  const gpiEst = kaufpreis * gpiEstSatz;
  const notar = kaufpreis * notarAnteil;
  const makler = kaufpreis * maklerAnteil;
  const gesamt = gpiEst + notar + makler;
  return {
    gpiEst: Math.round(gpiEst), gpiEstSatz,
    notar: Math.round(notar), makler: Math.round(makler),
    gesamt: Math.round(gesamt), prozent: gesamt / kaufpreis,
  };
}

/* ── Instandhaltung ── */
export function getInstandhaltungProQm(baujahr) {
  if (!baujahr) return 1.00;
  const alter = new Date().getFullYear() - baujahr;
  if (alter < 10) return 0.65;
  if (alter < 30) return 1.00;
  if (alter < 50) return 1.50;
  return 2.00;
}

/* ── Cashflow (vollständig) ── */
// Hauptberechnung: Cashflow OHNE Tilgung — konsistent mit Plugin-Logik.
// Bewirtschaftung: Pauschale 1,00 EUR/m² wie Plugin (areaSqm * 2.50 * 0.4).
// tilgungssatz wird NUR für cashflowNachTilgung (Simulation) verwendet.
export function calculateMonthlyCashflow({
  monatskaltmiete, darlehensbetrag, zinssatz, tilgungssatz = 0.02,
  wohnflaeche,
}) {
  // ALL inputs must be decimals (0.035, not 3.5)
  const davonZins = darlehensbetrag * zinssatz / 12;
  const davonTilgung = darlehensbetrag * tilgungssatz / 12;
  const kreditrate = davonZins + davonTilgung;

  // Bewirtschaftung pauschal wie Plugin: 1,00 EUR/m²
  const bewirtschaftung = wohnflaeche * 1.00;

  // Hauptcashflow = Cashflow vor Tilgung (wie Plugin)
  const cashflow = monatskaltmiete - davonZins - bewirtschaftung;

  return {
    einnahmen: Math.round(monatskaltmiete),
    kreditrate: Math.round(kreditrate),
    davonZins: Math.round(davonZins),
    davonTilgung: Math.round(davonTilgung),
    bewirtschaftung: Math.round(bewirtschaftung),
    gesamtkosten: Math.round(davonZins + bewirtschaftung), // OHNE Tilgung
    cashflow: Math.round(cashflow),
    cashflowJahr: Math.round(cashflow * 12),
    cashflowNachTilgung: Math.round(cashflow - davonTilgung),        // nur für Simulation
    cashflowNachTilgungJahr: Math.round((cashflow - davonTilgung) * 12), // nur für Simulation
  };
}

/* ── Renditen ── */
export function calcBruttoRendite(jahreskaltmiete, grossValue) {
  if (!grossValue || !jahreskaltmiete) return null;
  return (jahreskaltmiete / grossValue) * 100;
}

// cashflowJahr = Cashflow vor Tilgung (Hauptwert) — umbenennung nur intern zur Klarheit
export function calcEKRendite({ cashflowVorTilgungJahr, eigenkapital }) {
  if (!eigenkapital) return 0;
  return (cashflowVorTilgungJahr / eigenkapital) * 100;
}

export function calcFaktor(kaufpreis, jahreskaltmiete) {
  if (!jahreskaltmiete) return null;
  return Math.round((kaufpreis / jahreskaltmiete) * 10) / 10;
}

export function calcBreakEvenZins({ monatskaltmiete, darlehensbetrag, tilgungssatz, wohnflaeche }) {
  if (!darlehensbetrag || !monatskaltmiete) return null;
  // Pauschale wie Plugin: 1,00 EUR/m²
  const bewirtschaftung = wohnflaeche * 1.00;
  const verfuegbar = monatskaltmiete - bewirtschaftung;
  if (verfuegbar <= 0) return 0;
  const breakEvenZins = (verfuegbar * 12 / darlehensbetrag) - tilgungssatz;
  return Math.max(breakEvenZins, 0);
}

/* ── Baseline Builder ── */
export function buildBaseline(raw, market) {
  const kaufpreis = parseFloat(raw.PurchasePrice) || 0;
  const wohnflaeche = parseFloat(raw.Area) || 0;
  const baujahr = raw.YearOfConstruction ? parseInt(raw.YearOfConstruction) : null;
  const bundesland = raw.Google_Region || raw.State || '';
  // Immer Marktmiete verwenden — konsistent mit Plugin-Scoring.
  // BaseRent (Inseratsmiete) wird nicht priorisiert, da nicht immer verfügbar.
  const monatskaltmiete = (market?.avgRent && wohnflaeche) ? market.avgRent * wohnflaeche : 0;
  const ppSqm = wohnflaeche ? Math.round(kaufpreis / wohnflaeche) : null;

  // Normalize: API might return 0.035 or 3.5 — norm() handles both
  const rawZins = norm(raw.InterestRate);
  const zinssatz = (rawZins != null && rawZins > 0) ? rawZins : 0.035;
  const rawTilgung = norm(raw.Repayment ?? raw.Tilgung);
  const tilgungssatz = (rawTilgung != null && rawTilgung > 0) ? rawTilgung : 0.02;
  const maklerAnteil = norm(raw.AgentCosts) ?? 0.0357;
  const notarAnteil = norm(raw.NotaryCosts) ?? 0.02;

  const nk = calculateKaufnebenkosten(kaufpreis, bundesland, maklerAnteil, notarAnteil);
  const gesamtkosten = kaufpreis + nk.gesamt;

  // Eigenkapital: EUR-Modus (absoluter Betrag) vs. %-Modus (Quote)
  // Muss identisch zum Plugin resolveEquityRatio() funktionieren
  let eigenkapital;
  const equityMode = raw.EquityMode || 'pct';
  if (equityMode === 'eur' && raw.EquityAbsolute != null) {
    const absEK = parseFloat(raw.EquityAbsolute);
    eigenkapital = absEK > 0 ? Math.round(absEK) : Math.round(gesamtkosten * 0.20);
  } else {
    const rawEK = norm(raw.EquityRatio);
    const eigenkapitalAnteil = (rawEK != null && rawEK > 0) ? rawEK : 0.20;
    eigenkapital = Math.round(gesamtkosten * eigenkapitalAnteil);
  }
  const eigenkapitalAnteil = gesamtkosten > 0 ? eigenkapital / gesamtkosten : 0.20;
  const darlehensbetrag = gesamtkosten - eigenkapital;

  const cf = monatskaltmiete > 0 ? calculateMonthlyCashflow({
    monatskaltmiete, darlehensbetrag, zinssatz, tilgungssatz, wohnflaeche,
  }) : null;

  const jahreskaltmiete = monatskaltmiete * 12;
  const bruttoRendite = calcBruttoRendite(jahreskaltmiete, gesamtkosten);
  const ekRendite = cf ? calcEKRendite({
    // cashflow ist jetzt bereits Cashflow vor Tilgung
    cashflowVorTilgungJahr: cf.cashflowJahr, eigenkapital,
  }) : null;
  const faktor = calcFaktor(kaufpreis, jahreskaltmiete);
  const breakEvenZins = cf ? calcBreakEvenZins({
    monatskaltmiete, darlehensbetrag, tilgungssatz, wohnflaeche,
  }) : null;

  const marketOverMarket = ppSqm && market?.avg ? Math.round((ppSqm / market.avg - 1) * 100) : null;

  return {
    kaufpreis, wohnflaeche, baujahr, bundesland, monatskaltmiete, ppSqm,
    rooms: raw.Rooms || null, propertyType: raw.PropertyType || '',
    // Store as DECIMALS internally
    eigenkapitalAnteil, zinssatz, tilgungssatz, maklerAnteil,
    nk, gesamtkosten, eigenkapital, darlehensbetrag,
    cf, bruttoRendite, ekRendite, faktor, breakEvenZins,
    marketOverMarket, score: raw.Score ?? null,
  };
}
